import { Fragment, useEffect, useState } from 'react';

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/popover';
import { ScrollArea } from '@art-of-hospitality-solutions-llc/ui-kit/components/scroll-area';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tabs';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tooltip';
import { DEFAULT_SWATCHES } from '@Constants/colorSwatches';
import { resolveCssColor, resolveHexValue } from '@Utils/colorPicker';
import { type Swatches } from '@Types/colorSwatch';
import clsx from 'clsx';
import { ColorPickerInput } from '@art-of-hospitality-solutions-llc/ui-kit/components/color-picker-input';
import ColorPickerSwatch from './ColorPickerSwatch';
import { CustomColorPicker } from './CustomColorPicker';

export default function ColorPicker({
  initialColor,
  variant = 'background',
  swatches,
  themeSwatches,
  updateColor,
  tabIndex,
}: {
  initialColor: string | undefined;
  variant: 'text' | 'background' | 'border' | 'icon';
  swatches?: Swatches;
  themeSwatches?: Swatches;
  updateColor: (color: string) => void;
  tabIndex?: number;
}) {
  const resolvedInitColor = resolveCssColor(initialColor, {
    ...(swatches ?? {}),
    ...(themeSwatches ?? {}),
  } as Swatches);

  const [value, setValue] = useState(resolvedInitColor ?? '');
  const [inputValue, setInputValue] = useState(resolvedInitColor ?? '');

  const handleInputColorUpdate = (newColor: string) => {
    const resolvedColorValue = resolveHexValue(newColor);
    if (resolvedColorValue !== newColor) setInputValue(resolvedColorValue);
    setValue(resolvedColorValue);
    updateColor(resolvedColorValue);
  };

  const handleColorChange = (newColor: string) => {
    updateColor(newColor);
  };

  useEffect(() => {
    if (initialColor !== value) setValue(initialColor ?? '');
  }, [initialColor]);

  useEffect(() => {
    if (inputValue !== value) {
      setInputValue(resolvedInitColor ?? value);
      updateColor(value);
    }
  }, [value]);

  return (
    <Popover modal={true}>
      <PopoverTrigger
        className="rounded-lg focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-hidden"
        tabIndex={tabIndex}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="h-10 w-25 rounded-md border border-slate-200 bg-white p-1">
                <div className="h-full w-full overflow-hidden rounded-sm border border-slate-200">
                  <div
                    className="relative h-full w-full"
                    style={{
                      backgroundColor:
                        variant !== 'border' ? resolvedInitColor : undefined,
                      borderColor:
                        variant === 'border' ? resolvedInitColor : undefined,
                      borderWidth: variant === 'border' ? 5 : 0,
                    }}
                  >
                    {!value && (
                      <svg
                        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transform"
                        width="28"
                        height="28"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <line
                          x1="0"
                          y1="28"
                          x2="28"
                          y2="0"
                          stroke="red"
                          strokeWidth="3"
                        />
                      </svg>
                    )}
                  </div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>
                {variant === 'background'
                  ? 'Category color'
                  : variant === 'border'
                    ? 'Border color'
                    : variant === 'text'
                      ? 'Text color'
                      : 'Icon color'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </PopoverTrigger>
      <PopoverContent className="w-fit" tabIndex={tabIndex}>
        <Tabs defaultValue="swatches">
          <TabsList className="w-full">
            <TabsTrigger
              value="swatches"
              className="w-full"
              tabIndex={tabIndex}
            >
              Swatches
            </TabsTrigger>
            <TabsTrigger value="custom" className="w-full" tabIndex={tabIndex}>
              Custom color
            </TabsTrigger>
          </TabsList>
          <div className="my-2">
            <ColorPickerInput
              value={inputValue}
              onChange={setInputValue}
              onBlur={() => handleInputColorUpdate(inputValue)}
            />
          </div>
          <TabsContent value="swatches" tabIndex={tabIndex}>
            <ScrollArea className="h-[200px] w-[212px] rounded-lg bg-slate-100 p-2">
              {swatches && (
                <div className="mb-3">
                  <div className="m-0.5 cursor-default text-xs text-slate-500">
                    Custom colors
                  </div>
                  <div className="flex flex-wrap">
                    {Object.entries(swatches).map(([key, swatch]) => (
                      <ColorPickerSwatch
                        color={swatch.color}
                        key={key}
                        updateColor={() => updateColor(key)}
                        selected={key === value}
                        name={swatch.name}
                      />
                    ))}
                  </div>
                </div>
              )}
              {themeSwatches && (
                <div className="mb-3">
                  <div className="m-0.5 cursor-default text-xs text-slate-500">
                    Theme colors
                  </div>
                  <div className="flex flex-wrap">
                    {Object.entries(themeSwatches).map(([key, swatch]) => (
                      <ColorPickerSwatch
                        color={swatch.color}
                        key={key}
                        updateColor={() => updateColor(key)}
                        selected={key === value}
                        name={swatch.name}
                      />
                    ))}
                  </div>
                </div>
              )}
              {/* <div className="m-0.5 cursor-default text-xs text-slate-500">
                Default colors
              </div> */}
              <div
                className={clsx(
                  'flex flex-wrap',
                  (swatches ?? themeSwatches) &&
                    'border-t border-gray-300 pt-3',
                )}
              >
                {DEFAULT_SWATCHES.map((swatch) => {
                  if (swatch.id === 1) {
                    return (
                      <Fragment key={swatch.id}>
                        <ColorPickerSwatch
                          color={swatch.value}
                          updateColor={updateColor}
                          selected={swatch.value === value}
                          name={swatch.name}
                        />
                        <div
                          className="h-0 basis-full"
                          key={`${swatch.id}-break`}
                        ></div>
                      </Fragment>
                    );
                  }
                  return (
                    <ColorPickerSwatch
                      color={swatch.value}
                      key={swatch.id}
                      updateColor={updateColor}
                      selected={swatch.value === value}
                      // name={swatch.name}
                    />
                  );
                })}
              </div>
            </ScrollArea>
          </TabsContent>
          <TabsContent value="custom">
            <div className="w-[212px]">
              <CustomColorPicker
                color={value}
                onChange={handleColorChange}
                className="mx-auto"
                tabIndex={tabIndex}
              />
            </div>
          </TabsContent>
        </Tabs>
      </PopoverContent>
    </Popover>
  );
}
